'use client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogClose,
  DialogPortal,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '@eliadmin/core/src/components/ui/drawer';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import type { Drawer as DrawerPrimitive } from 'vaul';
import React, { type PropsWithChildren } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname } from 'next/navigation';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';

type ResponsiveDialogProps = PropsWithChildren<{
  // pick common props, that we use often
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Root>;
  dialogProps?: React.ComponentProps<typeof Dialog>;
}>;

export function ResponsiveDialog({ dialogProps, drawerProps, ...props }: ResponsiveDialogProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop) return <Dialog {...props} {...dialogProps} />;
  return <Drawer {...props} {...drawerProps} />;
}

type ResponsiveDialogContentProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // NEW: overlay class name (applies to DialogOverlay / DrawerOverlay)
  overlayClassName?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Content>;
  dialogProps?: React.ComponentProps<typeof DialogContent>;

  // OPTIONAL: granular overlay props if you ever need them
  drawerOverlayProps?: React.ComponentProps<typeof DrawerPrimitive.Overlay>;
  dialogOverlayProps?: React.ComponentProps<typeof DialogOverlay>;
}>;

export function ResponsiveDialogContent({
  dialogProps,
  drawerProps,
  overlayClassName,
  dialogOverlayProps,
  drawerOverlayProps,
  ...props
}: ResponsiveDialogContentProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <>
        <DialogOverlay
          {...dialogOverlayProps}
          className={cn(overlayClassName, dialogOverlayProps?.className)}
        />
        <DialogContent
          {...props}
          {...dialogProps}
          className={cn(props.className, dialogProps?.className)}
        />
      </>
    );

  return (
    <>
      <DrawerOverlay
        {...drawerOverlayProps}
        className={cn(overlayClassName, drawerOverlayProps?.className)}
      />
      <DrawerContent
        {...props}
        {...drawerProps}
        className={cn(props.className, drawerProps?.className)}
      />
    </>
  );
}

type ResponsiveDialogOverlayProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Overlay>;
  dialogProps?: React.ComponentProps<typeof DialogOverlay>;
}>;

export function ResponsiveDialogOverlay({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogOverlayProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <DialogOverlay
        {...props}
        {...dialogProps}
        className={cn(props.className, dialogProps?.className)}
      />
    );
  return (
    <DrawerOverlay
      {...props}
      {...drawerProps}
      className={cn(props.className, drawerProps?.className)}
    />
  );
}

type ResponsiveDialogHeaderProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.HTMLAttributes<HTMLDivElement>;
  dialogProps?: React.HTMLAttributes<HTMLDivElement>;
}>;

export function ResponsiveDialogHeader({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogHeaderProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <DialogHeader
        {...props}
        {...dialogProps}
        className={cn(props.className, dialogProps?.className)}
      />
    );
  return (
    <DrawerHeader
      {...props}
      {...drawerProps}
      className={cn(props.className, drawerProps?.className)}
    />
  );
}

type ResponsiveDialogFooterProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.HTMLAttributes<HTMLDivElement>;
  dialogProps?: React.HTMLAttributes<HTMLDivElement>;
}>;

export function ResponsiveDialogFooter({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogFooterProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <DialogFooter
        {...props}
        {...dialogProps}
        className={cn(props.className, dialogProps?.className)}
      />
    );
  return (
    <DrawerFooter
      {...props}
      {...drawerProps}
      className={cn(props.className, drawerProps?.className)}
    />
  );
}

type ResponsiveDialogTitleProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Title>;
  dialogProps?: React.ComponentProps<typeof DialogTitle>;
}>;

export function ResponsiveDialogTitle({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogTitleProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <DialogTitle
        {...props}
        {...dialogProps}
        className={cn(props.className, dialogProps?.className)}
      />
    );
  return (
    <DrawerTitle
      {...props}
      {...drawerProps}
      className={cn(props.className, drawerProps?.className)}
    />
  );
}

type ResponsiveDialogDescriptionProps = PropsWithChildren<{
  // pick common props, that we use often
  className?: string;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Description>;
  dialogProps?: React.ComponentProps<typeof DialogDescription>;
}>;

export function ResponsiveDialogDescription({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogDescriptionProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop)
    return (
      <DialogDescription
        {...props}
        {...dialogProps}
        className={cn(props.className, dialogProps?.className)}
      />
    );
  return (
    <DrawerDescription
      {...props}
      {...drawerProps}
      className={cn(props.className, drawerProps?.className)}
    />
  );
}

type ResponsiveDialogTriggerProps = PropsWithChildren<{
  // pick common props, that we use often
  asChild?: boolean;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Trigger>;
  dialogProps?: React.ComponentProps<typeof DialogTrigger>;
}>;

export function ResponsiveDialogTrigger({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogTriggerProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop) return <DialogTrigger {...props} {...dialogProps} />;
  return <DrawerTrigger {...props} {...drawerProps} />;
}

type ResponsiveDialogCloseProps = PropsWithChildren<{
  // pick common props, that we use often
  asChild?: boolean;

  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Close>;
  dialogProps?: React.ComponentProps<typeof DialogClose>;
}>;

export function ResponsiveDialogClose({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogCloseProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop) return <DialogClose {...props} {...dialogProps} />;
  return <DrawerClose {...props} {...drawerProps} />;
}

type ResponsiveDialogPortalProps = PropsWithChildren<{
  // for better granular control over other not-so-often used props
  drawerProps?: React.ComponentProps<typeof DrawerPrimitive.Portal>;
  dialogProps?: React.ComponentProps<typeof DialogPortal>;
}>;

export function ResponsiveDialogPortal({
  dialogProps,
  drawerProps,
  ...props
}: ResponsiveDialogPortalProps) {
  const isDesktop = useBreakpoint('sm');

  if (isDesktop) return <DialogPortal {...props} {...dialogProps} />;
  return <DrawerPortal {...props} {...drawerProps} />;
}

type DialogConfig = {
  id: string;
  content: React.ReactNode;
  onClose?: () => void;
};

type DialogStoreState = {
  dialogs: Record<string, DialogConfig>;
};

type DialogStoreActions = {
  open: (config: DialogConfig) => void;
  close: (id: string) => void;
  closeAll: () => void;
};

const [ResponsiveDialogContextProvider, useResponsiveDialogStore] = createContextStore<
  DialogStoreState,
  DialogStoreActions
>(
  {
    dialogs: {},
  },
  set => ({
    open: (config: DialogConfig) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [config.id]: config,
        },
      })),
    close: (id: string) =>
      set(state => {
        const dialogs = { ...state.dialogs };
        delete dialogs[id];
        return { dialogs };
      }),
    closeAll: () =>
      set(() => ({
        dialogs: {},
      })),
  }),
);

export function ResponsiveDialogProvider(props: React.PropsWithChildren) {
  return (
    <ResponsiveDialogContextProvider>
      <ResponsiveDialogProviderRenderer />
      {props.children}
    </ResponsiveDialogContextProvider>
  );
}

function ResponsiveDialogProviderRenderer() {
  const dialogs = useResponsiveDialogStore(s => s.dialogs);
  const close = useResponsiveDialogStore(s => s.close);
  const closeAll = useResponsiveDialogStore(s => s.closeAll);

  const pathname = usePathname();

  React.useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {Object.values(dialogs).map(dialog => (
        <ResponsiveDialog
          key={dialog.id}
          open
          onOpenChange={open => {
            if (!open) {
              dialog.onClose?.();
              close(dialog.id);
            }
          }}
        >
          {dialog.content}
        </ResponsiveDialog>
      ))}
    </>
  );
}

export { useResponsiveDialogStore };
export type { DialogConfig };
